<template>
  <div class="col-12 col-xl-12">
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ t('grades.my_grades_title') }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner v-if="isLoading && gradeSheets.length === 0" :message="t('grades.loading')" class="py-5" />

          <template v-else>
            <div class="d-block d-xl-none">
              <div v-if="!isLoading && gradeSheets.length === 0" class="text-center py-3 text-muted">
                <i class="fas fa-file-alt fa-2x mb-2"></i>
                <p>{{ t('grades.no_grade_sheets') }}</p>
              </div>

              <div v-for="sheet in normalizedGradeSheets" :key="'mobile-' + sheet.id" class="card mb-3 mx-2">
                <div class="card-body py-2">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0 text-sm">{{ sheet.title }}</h6>
                    <span class="badge badge-sm bg-primary">
                      {{ sheet.subject?.name || t('grades.not_specified') }}
                    </span>
                  </div>
                  
                  <div class="mt-2">
                    <p class="text-xs text-muted mb-1">
                      {{ t('grades.class') }}: {{ sheet.class?.name || t('grades.not_specified') }}
                    </p>
                    <p class="text-xs mb-1">
                      {{ t('grades.total') }}: {{ getStudentGrade(sheet) }}
                    </p>
                  </div>
                  
               
                </div>
              </div>
            </div>

            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('grades.grade_sheet_title') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('grades.subject') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('grades.class') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('grades.total') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!isLoading && gradeSheets.length === 0">
                  <td colspan="5" class="text-center py-4 text-muted">
                    <i class="fas fa-file-alt fa-2x mb-2"></i>
                    <p>{{ t('grades.no_grade_sheets') }}</p>
                  </td>
                </tr>
                
                <template v-if="!isLoading">
                  <tr v-for="sheet in normalizedGradeSheets" :key="sheet.id">
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-md">{{ sheet.title }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">
                        {{ sheet.subject?.name || t('grades.not_specified') }}
                      </p>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">
                        {{ sheet.class?.name || t('grades.not_specified') }}
                      </p>
                    </td>
                    <td class="font-weight-bold">
                      {{ getStudentGrade(sheet) }}
                    </td>

                  </tr>
                </template>
              </tbody>
            </table>
            
            <div class="d-flex justify-content-center mt-4">
              <Pagination
                :pagination="pagination"
                @page-changed="onPageChanged"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import Pagination from "@/components/common/PaginationControls.vue";
import api from "@/api";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const store = useStore();
const toast = useToast();
const currentUser = store.state.user;

const gradeSheets = ref([]);
const isLoading = ref(false);
const pagination = ref({
  currentPage: 1,
  perPage: 10,
  totalItems: 0,
  totalPages: 0,
  from: 0,
  to: 0
});

// Normalize grade sheets data to ensure all required properties exist
const normalizedGradeSheets = computed(() => {
  if (!Array.isArray(gradeSheets.value)) return [];
  
  return gradeSheets.value.map(sheet => ({
    ...sheet,
    subject: sheet.subject || { id: null, name: t('grades.not_specified') },
    class: sheet.class || { id: null, name: t('grades.not_specified') },
    structures: sheet.structures?.map(structure => ({
      ...structure,
      contents: structure.contents || []
    })) || []
  }));
});

const getStudentGrade = (sheet) => {
  let total = 0;
  if (Array.isArray(sheet.structures)) {
    sheet.structures.forEach(structure => {
      const contents = Array.isArray(structure.contents) ? structure.contents : [];
      const content = contents.find(c => c?.student?.id === currentUser.id);
      if (content?.degree) {
        total += parseFloat(content.degree) || 0;
      }
    });
  }
  return total.toFixed(2);
};


const fetchGradeSheets = async () => {
  isLoading.value = true;
  try {
    const response = await api.get('/student/student-grade-sheets', {
      params: {
        student_id: currentUser.id,
        page: pagination.value.currentPage,
        per_page: pagination.value.perPage
      }
    });
    
    gradeSheets.value = Array.isArray(response.data?.data) ? response.data.data : [];
    
    if (response.data?.meta) {
      pagination.value = {
        currentPage: response.data.meta.current_page || 1,
        perPage: response.data.meta.per_page || 10,
        totalItems: response.data.meta.total || 0,
        totalPages: response.data.meta.last_page || 1,
        from: response.data.meta.from || 0,
        to: response.data.meta.to || 0
      };
    }
  } catch (error) {
    console.error('Failed to fetch grade sheets:', error);
    gradeSheets.value = [];
    const errorMsg = error.response?.data?.message || t('grades.load_failed');
    toast.error(errorMsg);
  } finally {
    isLoading.value = false;
  }
};

const onPageChanged = (page) => {
  pagination.value.currentPage = page;
  fetchGradeSheets();
};

onMounted(() => {
  fetchGradeSheets();
});
</script>